/**
 * Said when a newer version of the app opened in another tab and took the
 * device's database (`OutboxSupersededError`).
 *
 * Persistent and non-modal: a dialog would be dismissed and the next tap would
 * be refused again with nothing on screen. It stays until the page reloads onto
 * the newer build, which is the only thing that clears it.
 *
 * Reloading goes through the platform adapter, as the ErrorBoundary's does,
 * because a native shell has no reload button. The button is 44px under
 * ADR-0064: it is tapped once, outside the book's tap area, never mid-possession.
 */

import { navigation } from '@good-season/platform';
import { supportCodeLine } from '@good-season/ui';

import { noticeSentences, type SupersededOutcome } from './supersededCopy.js';
import { mayBeFinal } from './unavailable.js';

export {
  FINISH_CONSEQUENCE,
  REOPEN_CONSEQUENCE,
  TAKEOVER_ENTRY_SHORT,
  TAKEOVER_SHORT,
  TAKEOVER_UNCERTAIN_SHORT,
  type SupersededOutcome,
} from './supersededCopy.js';

/** The notice's words followed by the one support-code line. */
export function noticeBody(
  outcomes: readonly SupersededOutcome[],
  diagnostic: string,
  uncertain = false,
  bookOnScreen = true,
  finalOnServer: boolean = mayBeFinal(outcomes),
): string {
  return `${noticeSentences(outcomes, uncertain, bookOnScreen, finalOnServer)} ${supportCodeLine(diagnostic)}`;
}

export function SupersededNotice({
  diagnostic,
  outcomes,
  uncertain = false,
  bookOnScreen,
  finalOnServer,
}: {
  readonly diagnostic: string;
  readonly outcomes: readonly SupersededOutcome[];
  /**
   * Whether the game is or may be final on the server, when the caller knows
   * more than `outcomes` says (a plain Finish leaves no status sentence but
   * still closes the game). Defaults to what `outcomes` implies.
   */
  readonly finalOnServer?: boolean;
  /** The stop came from a reopen, so the store may be gone (`storeMayBeGone`). */
  readonly uncertain?: boolean;
  /** Whether the live book is showing beneath the notice. */
  readonly bookOnScreen: boolean;
}) {
  return (
    <section className="sb__notice" role="alert">
      <h2 className="sb__noticeTitle">A newer version of Good Season opened in another tab.</h2>
      <p className="sb__noticeBody">
        {noticeBody(
          outcomes,
          diagnostic,
          uncertain,
          bookOnScreen,
          finalOnServer ?? mayBeFinal(outcomes),
        )}
      </p>
      <button
        className="sb__messageAction sb__noticeAction"
        onClick={() => {
          navigation.restart();
        }}
        type="button"
      >
        Reload this page
      </button>
    </section>
  );
}
